import { makeAuthProvider } from '@zg-rentals/auth-react';

export type CometUser = {
  email?: string;
  hasStrongAuthentication?: boolean;
  impersonatorEmail?: string;
  impersonatorUserToken?: string;
  isLoggedIn: boolean;
  isTwoFactorAuthEnabled?: boolean;
  isTwoFactorAuthenticated?: boolean;
  roles?: Array<string>;
  userToken?: string;
};

export type CometAuthProviderProps<Props> = Props & {
  allRequiredRoles?: Array<string>;
  oneOfRequiredRoles?: Array<string>;
  requireStrongAuthentication?: boolean;
  isAuth0mfaRequiredExp?: boolean;
};

function isLoggedIn<User extends CometUser>(
  {
    impersonatorEmail,
    impersonatorUserToken,
    isLoggedIn,
    hasStrongAuthentication,
    isTwoFactorAuthenticated,
    isTwoFactorAuthEnabled,
    userToken,
    email,
  }: User,
  { requireStrongAuthentication, isAuth0mfaRequiredExp }: CometAuthProviderProps<unknown>,
) {
  const isImpersonating = impersonatorEmail && impersonatorUserToken;

  if (!isLoggedIn || (!isImpersonating && isLoggedIn && !email && !userToken)) {
    return false;
  } else if (isImpersonating) {
    return true;
  } else if (isAuth0mfaRequiredExp && (!isTwoFactorAuthEnabled || !isTwoFactorAuthenticated)) {
    return false;
  } else if (requireStrongAuthentication && !hasStrongAuthentication) {
    return false;
  } else {
    return true;
  }
}

function hasRoles<User extends CometUser>(
  user: User,
  { allRequiredRoles, oneOfRequiredRoles }: CometAuthProviderProps<unknown>,
) {
  const userRoles = user.roles ?? [];

  if (allRequiredRoles?.length && !allRequiredRoles.every((r) => userRoles.includes(r))) {
    return false;
  }

  if (oneOfRequiredRoles?.length && !oneOfRequiredRoles.some((r) => userRoles.includes(r))) {
    return false;
  }

  return true;
}

export function makeCometAuthProvider<Props, User extends CometUser>(
  fetchUser: (props: CometAuthProviderProps<Props>) => Promise<User>,
) {
  return makeAuthProvider<CometAuthProviderProps<Props>, User>({
    fetchUser,

    authorizeUser: (user, props) => {
      if (!isLoggedIn(user, props)) {
        return false;
      }

      return hasRoles(user, props);
    },
  });
}

export function isAuthorized<U>({
  mapUser,
  ...options
}: {
  mapUser: (user: U) => CometUser;

  allRequiredRoles?: Array<string>;
  oneOfRequiredRoles?: Array<string>;
  requireStrongAuthentication?: boolean;
  isAuth0mfaRequiredExp?: boolean;
}) {
  return (user: U) => isLoggedIn(mapUser(user), options);
}
